// src/components/common/Forms/FormField.tsx

import {forwardRef, useState} from "react";
import {Input, Select, Button} from "../../ui";
import {Calendar, Upload, X, Eye, EyeOff} from "lucide-react";
import {clsx} from "clsx";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Base form field props
export interface BaseFormFieldProps {
  name: string;
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
}

// Text input field
export interface TextFieldProps extends BaseFormFieldProps {
  type: "text" | "email" | "password" | "url" | "tel";
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  autoComplete?: string;
}

// Number input field
export interface NumberFieldProps extends BaseFormFieldProps {
  type: "number";
  value?: number;
  onChange?: (value: number | undefined) => void;
  onBlur?: () => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
}

// Textarea field
export interface TextareaFieldProps extends BaseFormFieldProps {
  type: "textarea";
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  minLength?: number;
  resize?: boolean;
}

// Select field
export interface SelectFieldProps extends BaseFormFieldProps {
  type: "select";
  value?: string | number;
  onChange?: (value: string | number) => void;
  onBlur?: () => void;
  options: Array<{value: string | number; label: string; disabled?: boolean}>;
  placeholder?: string;
  multiple?: boolean;
  searchable?: boolean;
}

// Multi-select field
export interface MultiSelectFieldProps extends BaseFormFieldProps {
  type: "multiselect";
  value?: (string | number)[];
  onChange?: (value: (string | number)[]) => void;
  onBlur?: () => void;
  options: Array<{value: string | number; label: string; disabled?: boolean}>;
  placeholder?: string;
  maxSelections?: number;
}

// Radio group field
export interface RadioFieldProps extends BaseFormFieldProps {
  type: "radio";
  value?: string | number;
  onChange?: (value: string | number) => void;
  onBlur?: () => void;
  options: Array<{value: string | number; label: string; disabled?: boolean}>;
  direction?: "horizontal" | "vertical";
}

// Checkbox field
export interface CheckboxFieldProps extends BaseFormFieldProps {
  type: "checkbox";
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  onBlur?: () => void;
  checkboxLabel?: string;
}

// Checkbox group field
export interface CheckboxGroupFieldProps extends BaseFormFieldProps {
  type: "checkbox-group";
  value?: (string | number)[];
  onChange?: (value: (string | number)[]) => void;
  onBlur?: () => void;
  options: Array<{value: string | number; label: string; disabled?: boolean}>;
  direction?: "horizontal" | "vertical";
  maxSelections?: number;
}

// Date field
export interface DateFieldProps extends BaseFormFieldProps {
  type: "date" | "datetime-local" | "time";
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  min?: string;
  max?: string;
}

// File upload field
export interface FileFieldProps extends BaseFormFieldProps {
  type: "file";
  value?: File[];
  onChange?: (files: File[]) => void;
  onBlur?: () => void;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number; // in bytes
  preview?: boolean;
}

// Switch/Toggle field
export interface SwitchFieldProps extends BaseFormFieldProps {
  type: "switch";
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  onBlur?: () => void;
  switchLabel?: string;
  size?: "sm" | "md" | "lg";
}

// Rich text editor field (placeholder for future implementation)
export interface RichTextFieldProps extends BaseFormFieldProps {
  type: "richtext";
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  toolbar?: string[];
}

// Union type for all form field props
export type FormFieldProps =
  | TextFieldProps
  | NumberFieldProps
  | TextareaFieldProps
  | SelectFieldProps
  | MultiSelectFieldProps
  | RadioFieldProps
  | CheckboxFieldProps
  | CheckboxGroupFieldProps
  | DateFieldProps
  | FileFieldProps
  | SwitchFieldProps
  | RichTextFieldProps;

const FormField = forwardRef<HTMLElement, FormFieldProps>((props, ref) => {
  const {
    name,
    label,
    error,
    helperText,
    required,
    disabled,
    className = "",
    labelClassName = "",
    type,
  } = props;

  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);

  // File upload state
  const [dragOver, setDragOver] = useState(false);

  const renderLabel = () => {
    if (!label) return null;

    return (
      <label
        htmlFor={name}
        className={clsx(
          "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2",
          labelClassName
        )}
      >
        {label}
        {required && <span className="text-danger-500 ml-1">*</span>}
      </label>
    );
  };

  const renderError = () => {
    if (!error) return null;

    return (
      <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">
        {error}
      </p>
    );
  };

  const renderHelperText = () => {
    if (!helperText || error) return null;

    return (
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {helperText}
      </p>
    );
  };

  const renderField = () => {
    switch (type) {
      case "text":
      case "email":
      case "url":
      case "tel": {
        const textProps = props as TextFieldProps;
        return (
          <Input
            ref={ref as React.Ref<HTMLInputElement>}
            id={name}
            name={name}
            type={type}
            value={textProps.value || ""}
            onChange={(e) => textProps.onChange?.(e.target.value)}
            placeholder={textProps.placeholder}
            disabled={disabled}
            error={error}
            leftIcon={textProps.leftIcon}
            rightIcon={textProps.rightIcon}
            maxLength={textProps.maxLength}
            minLength={textProps.minLength}
            pattern={textProps.pattern}
            autoComplete={textProps.autoComplete}
          />
        );
      }

      case "password": {
        const passwordProps = props as TextFieldProps;
        return (
          <Input
            ref={ref as React.Ref<HTMLInputElement>}
            id={name}
            name={name}
            type={showPassword ? "text" : "password"}
            value={passwordProps.value || ""}
            onChange={(e) => passwordProps.onChange?.(e.target.value)}
            placeholder={passwordProps.placeholder}
            disabled={disabled}
            error={error}
            leftIcon={passwordProps.leftIcon}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            }
            maxLength={passwordProps.maxLength}
            minLength={passwordProps.minLength}
            autoComplete={passwordProps.autoComplete}
          />
        );
      }

      case "number": {
        const numberProps = props as NumberFieldProps;
        return (
          <Input
            ref={ref as React.Ref<HTMLInputElement>}
            id={name}
            name={name}
            type="number"
            value={numberProps.value?.toString() || ""}
            onChange={(e) => {
              const value = e.target.value;
              numberProps.onChange?.(value ? Number(value) : undefined);
            }}
            placeholder={numberProps.placeholder}
            disabled={disabled}
            error={error}
            min={numberProps.min}
            max={numberProps.max}
            step={numberProps.step}
          />
        );
      }

      case "textarea": {
        const textareaProps = props as TextareaFieldProps;
        return (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            id={name}
            name={name}
            value={textareaProps.value || ""}
            onChange={(e) => textareaProps.onChange?.(e.target.value)}
            placeholder={textareaProps.placeholder}
            disabled={disabled}
            rows={textareaProps.rows || 4}
            maxLength={textareaProps.maxLength}
            minLength={textareaProps.minLength}
            className={clsx(
              "form-input",
              !textareaProps.resize && "resize-none",
              error &&
                "border-danger-500 focus:border-danger-500 focus:ring-danger-500"
            )}
          />
        );
      }

      case "select": {
        const selectProps = props as SelectFieldProps;
        return (
          <Select
            ref={ref as React.Ref<HTMLSelectElement>}
            id={name}
            name={name}
            value={selectProps.value?.toString() || ""}
            onChange={(e) => {
              const value = e.target.value;
              selectProps.onChange?.(
                isNaN(Number(value)) ? value : Number(value)
              );
            }}
            disabled={disabled}
            error={error ?? ""}
            options={selectProps.options.map((opt) => {
              const option = {
                value: opt.value.toString(),
                label: opt.label,
              };
              if (typeof opt.disabled !== "undefined") {
                (option as any).disabled = opt.disabled;
              }
              return option;
            })}
          />
        );
      }

      case "multiselect": {
        const multiSelectProps = props as MultiSelectFieldProps;
        return (
          <div className="space-y-2">
            {multiSelectProps.options.map((option) => (
              <label key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={
                    multiSelectProps.value?.includes(option.value) || false
                  }
                  onChange={(e) => {
                    const currentValue = multiSelectProps.value || [];
                    let newValue: (string | number)[];

                    if (e.target.checked) {
                      newValue = [...currentValue, option.value];
                    } else {
                      newValue = currentValue.filter((v) => v !== option.value);
                    }

                    multiSelectProps.onChange?.(newValue);
                  }}
                  disabled={disabled || option.disabled}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );
      }

      case "radio": {
        const radioProps = props as RadioFieldProps;
        return (
          <div
            className={clsx(
              "space-y-2",
              radioProps.direction === "horizontal" &&
                "flex space-x-4 space-y-0"
            )}
          >
            {radioProps.options.map((option) => (
              <label key={option.value} className="flex items-center">
                <input
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={radioProps.value === option.value}
                  onChange={(e) => {
                    const value = e.target.value;
                    radioProps.onChange?.(
                      isNaN(Number(value)) ? value : Number(value)
                    );
                  }}
                  disabled={disabled || option.disabled}
                  className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );
      }

      case "checkbox": {
        const checkboxProps = props as CheckboxFieldProps;
        return (
          <label className="flex items-center">
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              id={name}
              name={name}
              type="checkbox"
              checked={checkboxProps.checked || false}
              onChange={(e) => checkboxProps.onChange?.(e.target.checked)}
              disabled={disabled}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            {checkboxProps.checkboxLabel && (
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {checkboxProps.checkboxLabel}
              </span>
            )}
          </label>
        );
      }

      case "checkbox-group": {
        const checkboxGroupProps = props as CheckboxGroupFieldProps;
        return (
          <div
            className={clsx(
              "space-y-2",
              checkboxGroupProps.direction === "horizontal" &&
                "flex flex-wrap gap-4 space-y-0"
            )}
          >
            {checkboxGroupProps.options.map((option) => (
              <label key={option.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={
                    checkboxGroupProps.value?.includes(option.value) || false
                  }
                  onChange={(e) => {
                    const currentValue = checkboxGroupProps.value || [];
                    let newValue: (string | number)[];

                    if (e.target.checked) {
                      newValue = [...currentValue, option.value];
                    } else {
                      newValue = currentValue.filter((v) => v !== option.value);
                    }

                    checkboxGroupProps.onChange?.(newValue);
                  }}
                  disabled={disabled || option.disabled}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );
      }

      case "date":
      case "datetime-local":
      case "time": {
        const dateProps = props as DateFieldProps;
        return (
          <Input
            ref={ref as React.Ref<HTMLInputElement>}
            id={name}
            name={name}
            type={type}
            value={dateProps.value || ""}
            onChange={(e) => dateProps.onChange?.(e.target.value)}
            disabled={disabled}
            error={error}
            min={dateProps.min}
            max={dateProps.max}
            leftIcon={<Calendar className="w-4 h-4" />}
          />
        );
      }

      case "file": {
        const fileProps = props as FileFieldProps;

        const handleFileChange = (files: FileList | null) => {
          if (!files) return;

          const fileArray = Array.from(files);

          // Filter by accept type
          let filteredFiles = fileArray;
          if (fileProps.accept) {
            const acceptTypes = fileProps.accept
              .split(",")
              .map((t) => t.trim());
            filteredFiles = fileArray.filter((file) =>
              acceptTypes.some((acceptType) => {
                if (acceptType.startsWith(".")) {
                  return file.name
                    .toLowerCase()
                    .endsWith(acceptType.toLowerCase());
                }
                return file.type.match(acceptType.replace("*", ".*"));
              })
            );
          }

          // Check max files
          if (fileProps.maxFiles && filteredFiles.length > fileProps.maxFiles) {
            filteredFiles = filteredFiles.slice(0, fileProps.maxFiles);
          }

          // Check file sizes
          if (fileProps.maxSize) {
            filteredFiles = filteredFiles.filter(
              (file) => file.size <= fileProps.maxSize!
            );
          }

          fileProps.onChange?.(filteredFiles);
        };

        const handleDrop = (e: React.DragEvent) => {
          e.preventDefault();
          setDragOver(false);
          handleFileChange(e.dataTransfer.files);
        };

        const handleDragOver = (e: React.DragEvent) => {
          e.preventDefault();
          setDragOver(true);
        };

        const handleDragLeave = (e: React.DragEvent) => {
          e.preventDefault();
          setDragOver(false);
        };

        return (
          <div className="space-y-4">
            <div
              className={clsx(
                "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
                dragOver
                  ? "border-primary-400 bg-primary-50 dark:bg-primary-900/20"
                  : "border-gray-300 dark:border-dark-600 hover:border-gray-400 dark:hover:border-dark-500",
                disabled && "opacity-50 cursor-not-allowed"
              )}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-4" />
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Drop files here or{" "}
                  <label className="text-primary-600 hover:text-primary-500 cursor-pointer">
                    browse
                    <input
                      ref={ref as React.Ref<HTMLInputElement>}
                      id={name}
                      name={name}
                      type="file"
                      accept={fileProps.accept}
                      multiple={fileProps.multiple}
                      onChange={(e) => handleFileChange(e.target.files)}
                      disabled={disabled}
                      className="sr-only"
                    />
                  </label>
                </p>
                {fileProps.maxSize && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Max file size: {Math.round(fileProps.maxSize / 1024 / 1024)}
                    MB
                  </p>
                )}
                {fileProps.maxFiles && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Max files: {fileProps.maxFiles}
                  </p>
                )}
              </div>
            </div>

            {/* File preview */}
            {fileProps.preview &&
              fileProps.value &&
              fileProps.value.length > 0 && (
                <div className="space-y-2">
                  {fileProps.value.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-700 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded flex items-center justify-center">
                          <Upload className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {Math.round(file.size / 1024)}KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newFiles = fileProps.value!.filter(
                            (_, i) => i !== index
                          );
                          fileProps.onChange?.(newFiles);
                        }}
                        className="text-gray-400 hover:text-danger-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
          </div>
        );
      }

      case "switch": {
        const switchProps = props as SwitchFieldProps;
        const size = switchProps.size || "md";

        const sizeClasses = {
          sm: "h-5 w-9",
          md: "h-6 w-11",
          lg: "h-7 w-14",
        };

        const thumbSizeClasses = {
          sm: "h-4 w-4",
          md: "h-5 w-5",
          lg: "h-6 w-6",
        };

        return (
          <label className="flex items-center">
            <div className="relative">
              <input
                ref={ref as React.Ref<HTMLInputElement>}
                id={name}
                name={name}
                type="checkbox"
                checked={switchProps.checked || false}
                onChange={(e) => switchProps.onChange?.(e.target.checked)}
                disabled={disabled}
                className="sr-only"
              />
              <div
                className={clsx(
                  "block rounded-full transition-colors",
                  sizeClasses[size],
                  switchProps.checked
                    ? "bg-primary-600"
                    : "bg-gray-200 dark:bg-dark-600",
                  disabled && "opacity-50 cursor-not-allowed"
                )}
              >
                <div
                  className={clsx(
                    "absolute left-1 top-1 bg-white rounded-full transition-transform",
                    thumbSizeClasses[size],
                    switchProps.checked && "transform translate-x-5"
                  )}
                />
              </div>
            </div>
            {switchProps.switchLabel && (
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                {switchProps.switchLabel}
              </span>
            )}
          </label>
        );
      }

      case "richtext": {
        const richTextProps = props as RichTextFieldProps;
        return (
          <ReactQuill
            theme="snow"
            value={richTextProps.value || ""}
            onChange={(value) => richTextProps.onChange?.(value)}
            placeholder={richTextProps.placeholder}
            readOnly={disabled}
            modules={{
              toolbar: [
                [{header: [1, 2, false]}],
                ["bold", "italic", "underline", "strike", "blockquote"],
                [
                  {list: "ordered"},
                  {list: "bullet"},
                  {indent: "-1"},
                  {indent: "+1"},
                ],
                ["link", "image"],
                ["clean"],
              ],
            }}
            className={clsx(
              "h-32 mb-10", // Added height and margin-bottom for spacing
              error && "ql-container--error" // Custom class for error styling
            )}
          />
        );
      }

      default:
        return <div>Unsupported field type</div>;
    }
  };

  return (
    <div className={clsx("space-y-1", className)}>
      {renderLabel()}
      {renderField()}
      {renderError()}
      {renderHelperText()}
    </div>
  );
});

FormField.displayName = "FormField";

export default FormField;
